// @ts-strict-ignore
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Contact } from '../../crm/models/contact.model';
import * as contactActions from '../actions/contact.actions';
import { createReducer, on } from '@ngrx/store';
import { changeSortByListOrder } from '@fc-shared/utils/change-sort-by-list-order';
import { SortBy } from '@fc-shared/ui/sort-by/sort-by';
import { deleteCompanySuccess } from '../actions';

export interface State extends EntityState<Contact> {
  selectedContact: number;
  contactError: any;
  loading: boolean;
  total: number;
  current: number;
  loadingMore: boolean;
  search: string;
  sortBy: SortBy;
  sortByList: SortBy[];
}

export const adapter: EntityAdapter<Contact> = createEntityAdapter<Contact>();

export const initialState: State = adapter.getInitialState({
  selectedContact: null,
  contactError: null,
  loading: false,
  total: null,
  current: null,
  loadingMore: false,
  search: '',
  sortBy: { name: 'First Name', value: 'firstName', order: false },
  sortByList: [
    { name: 'First Name', value: 'firstName', order: false },
    { name: 'Last Name', value: 'lastName', order: null },
  ],
});

export const reducer = createReducer(
  initialState,
  on(contactActions.contactError, (state, action) => ({
    ...state,
    contactError: action.err,
    loading: false,
  })),
  on(contactActions.resetContactFilters, (state) => ({
    ...state,
    search: initialState.search,
    sortBy: initialState.sortBy,
    sortByList: initialState.sortByList,
  })),

  on(contactActions.selectContact, (state, action) => ({
    ...state,
    selectedContact: action.id,
  })),

  on(contactActions.setContactSearch, (state, action) => ({
    ...state,
    search: action.search,
  })),
  on(contactActions.setContactSort, (state, action) => ({
    ...state,
    sortBy: { ...action.sortBy, order: !action.sortBy.order },
    sortByList: changeSortByListOrder(state.sortByList, action.sortBy),
  })),
  on(contactActions.addContactSuccess, (state, action) => ({
    ...adapter.addOne(action.contact, state),
    loading: false,
  })),

  on(contactActions.upsertContact, (state, action) =>
    adapter.upsertOne(action.contact, state),
  ),

  on(contactActions.addContacts, (state, action) =>
    adapter.addMany(action.contacts, state),
  ),

  on(contactActions.upsertContacts, (state, action) =>
    adapter.upsertMany(action.contacts, state),
  ),

  on(contactActions.updateContactSuccess, (state, action) => ({
    ...adapter.updateOne(action.contact, state),
    loading: false,
  })),

  on(contactActions.updateContactsSuccess, (state, action) => ({
    ...adapter.updateMany(action.contacts, state),
    loading: false,
  })),

  on(contactActions.deleteContactSuccess, (state, action) => ({
    ...adapter.removeOne(action.id, state),
    selectedContact: null as number,
    loading: false,
  })),

  // if we delete company, we need to delete this company from all contacts
  on(deleteCompanySuccess, (state, { id }) => {
    const contactsUpdates = Object.values(state.entities)
      .filter((contact) => +contact.company === +id)
      .map((contact) => ({
        id: contact.id,
        changes: { company: null as number },
      }));

    if (contactsUpdates.length) {
      return adapter.updateMany(contactsUpdates, state);
    }
    return { ...state };
  }),

  on(
    contactActions.loadContacts,
    contactActions.updateContact,
    contactActions.deleteContact,
    contactActions.addContact,
    contactActions.updateContacts,
    (state) => ({
      ...state,
      loading: true,
    }),
  ),

  on(contactActions.loadContactsSuccess, (state, action) => ({
    ...adapter.addMany(action.contacts, state),
    loading: false,
  })),
  on(contactActions.cancelLoadContacts, (state) => ({
    ...state,
    loading: false,
  })),
  on(contactActions.loadMoreContacts, (state) => ({
    ...state,
    loadingMore: true,
  })),

  on(contactActions.loadMoreContactsSuccess, (state, action) => ({
    ...adapter.addMany(action.contactList, state),
    loadingMore: false,
  })),

  on(contactActions.clearContacts, (state) => adapter.removeAll(state)),
);

export const { selectIds, selectEntities, selectAll, selectTotal } =
  adapter.getSelectors();

export const getSelectedContact = (state: State) => state.selectedContact;
export const getContactError = (state: State) => state.contactError;
export const getContactLoading = (state: State) => state.loading;
export const getLoadingMore = (state: State) => state.loadingMore;
export const getContactSearch = (state: State) => state.search;
export const getContactSortBy = (state: State) => state.sortBy;
export const getContactSortByList = (state: State) => state.sortByList;
